import { getFirebaseClientAuth } from "@/lib/firebase-client-config";

export interface Res<T> {
    isOk: boolean;
    response?: T;
    error?: any;
    statusCode: number;
}
export interface IdLabel {
    id: string | number;
    label: string;
}

const adjustHeader = async (withCredentials: boolean, header1: any, header2: any) => {
    const extra: any = {}
    if (withCredentials) {
        const firebaseClientAuth = await getFirebaseClientAuth();
        if (!firebaseClientAuth) {
            console.error('firebaseClientAuth is null')
            throw new Error('firebaseClientAuth is null');
        }
        const jwt = await firebaseClientAuth.currentUser?.getIdToken();
        if (jwt) {
            extra['Authorization'] = "Bearer " + jwt;
        }
    }
    return { ...header1, ...header2, ...extra };
}

// Wrap fetch to log message when request is sent
// const wrappedFetch = (originalFetch => {
//     return async (...args: [input: RequestInfo, init?: RequestInit | undefined]) => {
//         const result = await originalFetch.apply(this, args);
//         console.log('wrappedFetch');
//         console.log(result);
//         return result;
//     };
// })(fetch);

// Wrap fetch to log message when request is sent
const wrappedFetch = async (input: RequestInfo, init?: RequestInit) => {

    const result = await fetch(input, init);
    return result;
}

export async function createResFromResponse<T>(res: Response) {
    let body = null;
    try {
        body = await res.json();
    } catch (e) {
        console.error('Error createResFromResponse', e);
    }

    return {
        statusCode: res.status,
        isOk: res.ok,
        response: res.ok ? (body ? body as unknown as T : undefined) : undefined,
        error: !res.ok ? body : null,
    }
}
export async function createResBlobFromResponse(res: Response) {
    if (res.ok) {
        return {
            statusCode: res.status,
            isOk: res.ok,
            response: await res.blob(),
            error: null,
        }
    } else {
        let body = null;
        try {
            body = await res.json();
        } catch (e) {
            console.log('createResBlobFromResponse', e);
        }
        return {
            statusCode: res.status,
            isOk: res.ok,
            response: undefined,
            error: body,
        }
    }

}

export function createExceptionRes(exception: any) {
    return {
        statusCode: 500,
        isOk: false,
        response: undefined,
        error: exception,
    }
}

export function createUrlWithQuery(url: string, data: any = null) {
    let newUrl = url;

    if (data && Object.keys(data).length > 0) {
        const params: string[] = [];

        Object.keys(data).forEach((key) => {
            const value = data[key];

            if (value === undefined || value === null) {
                return;
            }

            if (typeof value !== "object") {
                params.push(
                    `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
                );
            } else if (Array.isArray(value)) {
                for (let i = 0; i < value.length; i++) {
                    const item = value[i];

                    if (typeof item === "object" && item?.id !== undefined && item?.option !== undefined) {
                        params.push(
                            `${encodeURIComponent(`${key}[${item.id}]`)}=${encodeURIComponent(String(item.option))}`
                        );
                    } else {
                        params.push(
                            `${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`
                        );
                    }

                }
            }
        });

        if (params.length > 0) {
            newUrl += (url.indexOf("?") === -1 ? "?" : "&") + params.join("&");
        }
    }

    return newUrl;
}

export async function commonGetData(withCredential: boolean, headers = {}, url = "", data: any = {}, cache: number | undefined = undefined) {

    const newUrl = createUrlWithQuery(url, data);

    // Default options are marked with *
    const response = await wrappedFetch(newUrl, {
        method: "GET", // *GET, POST, PUT, DELETE, etc.
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        credentials: "same-origin", // include, *same-origin, omit
        headers: await adjustHeader(withCredential, headers, {
            "Content-Type": "application/json"
        }),
        next: {
            revalidate: cache,
        },
        redirect: "follow", // manual, *follow, error
        referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
        // body: JSON.stringify(data), // body data type must match "Content-Type" header
    });
    return response; // parses JSON response into native JavaScript objects
}


export async function commonGetFile(withCredential: boolean, headers = {}, url = "", data: any = {}) {
    const newUrl = createUrlWithQuery(url, data);

    // Default options are marked with *
    const response = await wrappedFetch(newUrl, {
        method: "GET", // *GET, POST, PUT, DELETE, etc.
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        credentials: "same-origin", // include, *same-origin, omit
        headers: await adjustHeader(withCredential, headers, {
            "Content-Type": "application/json"
        }),
        redirect: "follow", // manual, *follow, error
        referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
        // body: JSON.stringify(data), // body data type must match "Content-Type" header
    });
    return response; // parses JSON response into native JavaScript objects
}


export async function commonPostData(withCredential: boolean, headers = {}, url = "", data = {}) {
    const config: any = {
        method: "POST", // *GET, POST, PUT, DELETE, etc.
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        credentials: 'same-origin', // include, *same-origin, omit
        headers: await adjustHeader(withCredential, headers, {
            "Content-Type": "application/json"
        }),
        redirect: "follow", // manual, *follow, error
        referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
        // body data type must match "Content-Type" header
    };
    // if (Object.keys(data).length > 0) {
    config['body'] = JSON.stringify(data);
    // }

    // Default options are marked with *
    const response = await wrappedFetch(url, config);
    return response; // parses JSON response into native JavaScript objects
}

export async function commonPostDataWithForm(withCredential: boolean, headers = {}, url = "", data: any = {}) {

    const formData = new FormData();
    for (const key in data) {
        if (data.hasOwnProperty(key)) {
            formData.append(key, data[key]);
        }
    }
    // Default options are marked with *
    const response = await wrappedFetch(url, {
        method: "POST", // *GET, POST, PUT, DELETE, etc.
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        credentials: "same-origin", // include, *same-origin, omit
        headers: await adjustHeader(withCredential, headers, {}),
        redirect: "follow", // manual, *follow, error
        referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
        body: formData, // body data type must match "Content-Type" header
    });
    return response; // parses JSON response into native JavaScript objects
}

export async function commonPutDataWithForm(withCredential: boolean, headers = {}, url = "", data: any = {}) {

    const formData = new FormData();
    for (const key in data) {
        if (data.hasOwnProperty(key)) {
            formData.append(key, data[key]);
        }
    }
    // Default options are marked with *
    const response = await wrappedFetch(url, {
        method: "PUT", // *GET, POST, PUT, DELETE, etc.
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        credentials: "same-origin", // include, *same-origin, omit
        headers: await adjustHeader(withCredential, headers, {}),
        redirect: "follow", // manual, *follow, error
        referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
        body: formData, // body data type must match "Content-Type" header
    });
    return response; // parses JSON response into native JavaScript objects
}

export async function commonPatchData(withCredential: boolean, headers = {}, url = "", data = {}) {
    // Default options are marked with *
    const config: any = {
        method: "PATCH", // *GET, POST, PUT, DELETE, etc.
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        credentials: "same-origin", // include, *same-origin, omit
        headers: await adjustHeader(withCredential, headers, {
            "Content-Type": "application/json"
        }),
        redirect: "follow", // manual, *follow, error
        referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
        // body: JSON.stringify(data), // body data type must match "Content-Type" header
    };
    if (Object.keys(data).length > 0) {
        config['body'] = JSON.stringify(data);
    }
    const response = await wrappedFetch(url, config);
    return response; // parses JSON response into native JavaScript objects
}